import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Users,
  Building,
  MapPin,
  Phone,
  FileText,
  Download,
  SortAsc,
  SortDesc,
  Calendar,
  Target,
  Menu,
  Eye,
  EyeOff,
  Upload,
  Inbox,
  Clock,
  Send,
  CheckCircle2,
  FileDown
} from 'lucide-react';
import './App.css';
import AppSidebar from './components/AppSidebar';
import NotificationBell from './components/NotificationBell';
import { downloadClientPackage, isExternalLink } from './utils/clientPdf';
import { clearSession, logout as endSession } from './auth';
import { validateUserFields } from './utils/validation';

const CLIENT_STATUS_OPTIONS = [
  { value: 'inward', label: 'Inward', icon: Inbox },
  { value: 'in_progress', label: 'In Progress', icon: Clock },
  { value: 'outward', label: 'Outward', icon: Send },
  { value: 'completed', label: 'Completed', icon: CheckCircle2 }
];

const OVERVIEW_STATS = [
  { key: 'totalClients', label: 'Total Clients', icon: Users },
  { key: 'uniqueProjectHeads', label: 'Project Heads', icon: Target },
  { key: 'totalPlotArea', label: 'Total Plot Area', icon: MapPin, format: (v) => Number(v || 0).toFixed(2) },
  { key: 'activeAreas', label: 'Active Areas', icon: Building }
];

function StatCard({ icon: Icon, label, value, className = '' }) {
  return (
    <div className={`stat-card${className ? ` ${className}` : ''}`}>
      <div className="stat-card-icon" aria-hidden="true">
        <Icon size={20} />
      </div>
      <div className="stat-card-body">
        <p className="stat-card-label">{label}</p>
        <p className="stat-card-value">{value}</p>
      </div>
    </div>
  );
}

const formatClientStatusLabel = (status) => {
  const match = CLIENT_STATUS_OPTIONS.find((item) => item.value === status);
  return match ? match.label : 'Inward';
};

const ClientStatusBadge = ({ status }) => (
  <span className={`status-badge status-${status || 'inward'}`}>
    {formatClientStatusLabel(status)}
  </span>
);

// API: same-origin in production/Docker; localhost when running React dev server
const API_ROOT = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
axios.defaults.baseURL = API_ROOT;

// Attach auth token to axios
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
function AdminCreateUserButton({ open: controlledOpen, onClose } = {}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : internalOpen;

  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState('architect');
  const [loading, setLoading] = React.useState(false);
  const [showPwd, setShowPwd] = React.useState(false);


  const closeModal = () => {
    if (onClose) onClose();
    else setInternalOpen(false);
  };

  const createUser = async (e) => {
    e?.preventDefault?.();
    const fieldError = validateUserFields({ username, email, password });
    if (fieldError) {
      toast.error(fieldError);
      return;
    }
    try {
      setLoading(true);
      await axios.post('/api/users/verify-email', { email: email.trim() });
      const res = await axios.post('/api/users', {
        username: username.trim(),
        password,
        email: email.trim(),
        role
      });
      toast.success(res.data.message || 'User created. Login credentials sent to their email.');
      if (res.data.warning) toast.warning(res.data.warning);
      closeModal();
      setUsername('');
      setPassword('');
      setEmail('');
      setRole('architect');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to create user';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    // In uncontrolled mode, render the trigger button
    if (!isControlled) {
      return (
        <button className="btn btn-success" onClick={() => setInternalOpen(true)}>
          Create User
        </button>
      );
    }
    // In controlled mode, render nothing when closed
    return null;
  }

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Create New User</h2>
          <button className="close-btn" onClick={closeModal}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={createUser} className="modal-form">
          <div className="form-group">
            <label className="form-label">Username</label>
            <input
              className="form-control"
              value={username}
              onChange={e => setUsername(e.target.value.replace(/\s/g, ''))}
              placeholder="Enter username"
              minLength={3}
              maxLength={50}
              pattern="[a-zA-Z0-9._-]{3,50}"
              title="3–50 characters: letters, numbers, dots, hyphens, underscores"
              required
            />
            <small className="field-hint">3–50 characters · letters, numbers, . _ -</small>
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ display:'flex', gap:8 }}>
              <input
                type={showPwd ? 'text' : 'password'}
                className="form-control"
                value={password}
                onChange={e => setPassword(e.target.value.replace(/\s/g, ''))}
                placeholder="Enter password"
                minLength={6}
                maxLength={128}
                required
              />
              <button type="button" className="btn btn-outline" aria-label={showPwd ? 'Hide password' : 'Show password'} onClick={() => setShowPwd(v => !v)}>
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <small className="field-hint">At least 6 characters · no spaces</small>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={e => setEmail(e.target.value.trimStart())}
              placeholder="Enter email address"
              maxLength={255}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select className="form-control" value={role} onChange={e => setRole(e.target.value)}>
              <option value="architect">Architect</option>
              <option value="reviewer">Reviewer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={loading}>{loading ? 'Creating...' : 'Create User'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
// eslint-disable-next-line no-unused-vars
function ManageUsersModal({ open, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { id, username, role, password, showPwd }
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'architect', showPwd: false });
  const [searchTerm, setSearchTerm] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/users');
      setUsers(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) loadUsers(); }, [open, loadUsers]);

  const startEdit = (u) => setEditing({ id: u.id, username: u.username, email: u.email || '', role: u.role, password: '', showPwd: false });
  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;
    const fieldError = validateUserFields({
      username: editing.username,
      email: editing.email,
      password: editing.password,
      passwordOptional: true
    });
    if (fieldError) {
      toast.error(fieldError);
      return;
    }
    try {
      setLoading(true);
      const payload = { username: editing.username.trim(), email: editing.email.trim(), role: editing.role };
      if (editing.password) payload.password = editing.password;
      await axios.put(`/api/users/${editing.id}`, payload);
      toast.success('User updated successfully');
      setEditing(null);
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Update failed');
    } finally { setLoading(false); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`⚠️ Delete user "${u.username}"?\n\nThis action cannot be undone.`)) return;
    try {
      setLoading(true);
      await axios.delete(`/api/users/${u.id}`);
      toast.success('User deleted successfully');
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Delete failed');
    } finally { setLoading(false); }
  };

  const createUser = async () => {
    const fieldError = validateUserFields({
      username: newUser.username,
      email: newUser.email,
      password: newUser.password
    });
    if (fieldError) {
      toast.error(fieldError);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post('/api/users', {
        username: newUser.username.trim(),
        password: newUser.password,
        email: newUser.email.trim(),
        role: newUser.role
      });
      toast.success(res.data.message || 'User created. Login credentials sent to their email.');
      if (res.data.warning) toast.warning(res.data.warning);
      setNewUser({ username: '', email: '', password: '', role: 'architect', showPwd: false });
      setCreating(false);
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Create failed');
    } finally { setLoading(false); }
  };

  const sequentialUsers = [...users]
    .sort((a, b) => Number(a.id) - Number(b.id))
    .map((u, index) => ({ ...u, displayId: index + 1 }));

  const filteredUsers = sequentialUsers.filter(u =>
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(u.displayId).includes(searchTerm)
  );

  if (!open) return null;

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content manage-users">
        <div className="modal-header">
          <h2 className="modal-title">
            <Users size={24} /> User Management
          </h2>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Search and Create Section */}
        <div style={{ padding: '20px', borderBottom: '1px solid #e9ecef' }}>
          <div className="toolbar">
            <div className="search-input">
              <Search size={18} className="search-icon" />
              <input
                className="form-control"
                placeholder="Search users by username or role..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="btn btn-success" onClick={() => setCreating(true)} disabled={creating}>
              <Plus size={16} /> Add User
            </button>
          </div>

          {/* Create User Form */}
          {creating && (
            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#495057' }}>Create New User</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto auto auto', gap: '12px', alignItems: 'end' }}>
                <div>
                  <label className="form-label">Username</label>
                  <input
                    className="form-control"
                    value={newUser.username}
                    onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                    placeholder="Enter username"
                  />
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={newUser.email}
                    onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="form-label">Password</label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input
                      type={newUser.showPwd ? 'text' : 'password'}
                      className="form-control"
                      value={newUser.password}
                      onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => setNewUser({ ...newUser, showPwd: !newUser.showPwd })}
                      style={{ minWidth: '44px' }}
                    >
                      {newUser.showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="form-label">Role</label>
                  <select
                    className="form-control"
                    value={newUser.role}
                    onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                  >
                    <option value="architect">Architect</option>
                    <option value="reviewer">Reviewer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <button className="btn btn-success" onClick={createUser} disabled={loading}>
                  {loading ? 'Creating...' : 'Create'}
                </button>
                <button className="btn btn-secondary" onClick={() => setCreating(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Users Table */}
        <div className="modal-form">
          {loading && <div className="loading">Loading...</div>}
          {!loading && (
            <>
              <div style={{ padding: '16px 20px 8px', color: '#6c757d', fontSize: '14px' }}>
                Showing {filteredUsers.length} of {users.length} users
              </div>
              <div className="table-wrapper">
                <table className="table">
                  <colgroup>
                    <col style={{ width: '64px' }} />
                    <col />
                    <col />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '220px' }} />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '200px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Password</th>
                      <th>Created</th>
                      <th style={{ minWidth: 200 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id}>
                        <td>{u.displayId}</td>
                        <td>
                          {editing?.id === u.id ? (
                            <input
                              className="form-control"
                              value={editing.username}
                              onChange={e => setEditing({ ...editing, username: e.target.value })}
                              style={{ minWidth: '120px' }}
                            />
                          ) : (
                            <span style={{ fontWeight: '500' }}>{u.username}</span>
                          )}
                        </td>
                        <td>
                          {editing?.id === u.id ? (
                            <input
                              type="email"
                              className="form-control"
                              value={editing.email}
                              onChange={e => setEditing({ ...editing, email: e.target.value })}
                              style={{ minWidth: '180px' }}
                            />
                          ) : (
                            <span style={{ color: '#4a5568' }}>{u.email || '—'}</span>
                          )}
                        </td>
                        <td>
                          {editing?.id === u.id ? (
                            <select
                              className="form-control"
                              value={editing.role}
                              onChange={e => setEditing({ ...editing, role: e.target.value })}
                            >
                              <option value="architect">Architect</option>
                              <option value="reviewer">Reviewer</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span className={`badge ${u.role === 'admin' ? 'badge-danger' : u.role === 'reviewer' ? 'badge-warning' : 'badge-primary'}`}>
                              {u.role}
                            </span>
                          )}
                        </td>
                        <td>
                          {editing?.id === u.id ? (
                            <div style={{ display: 'flex', gap: '4px', minWidth: '140px' }}>
                              <input
                                type={editing.showPwd ? 'text' : 'password'}
                                className="form-control"
                                value={editing.password}
                                onChange={e => setEditing({ ...editing, password: e.target.value })}
                                placeholder="New password (optional)"
                              />
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => setEditing({ ...editing, showPwd: !editing.showPwd })}
                                style={{ minWidth: '44px' }}
                              >
                                {editing.showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: '12px' }}>••••••••</span>
                          )}
                        </td>
                        <td style={{ fontSize: '12px', color: '#6c757d' }}>
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="actions">
                          {editing?.id === u.id ? (
                            <>
                              <button className="btn btn-success" onClick={saveEdit} disabled={loading}>
                                Save
                              </button>
                              <button className="btn btn-secondary" onClick={cancelEdit}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-primary" onClick={() => startEdit(u)}>
                                <Edit size={14} /> Edit
                              </button>
                              <button
                                className="btn btn-danger"
                                onClick={() => deleteUser(u)}
                                disabled={u.id === JSON.parse(localStorage.getItem('user') || '{}').id}
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer with suggestions */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid #e9ecef', background: '#f8f9fa', fontSize: '13px', color: '#6c757d' }}>
          <strong>💡 Tips:</strong>
          • Use search to quickly find users
          • Admin users can manage all system settings
          • Password field is optional when editing (leave blank to keep current password)
          • You cannot delete your own account
        </div>
      </div>
    </div>
  );
}





axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      clearSession();
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentRole = localStorage.getItem('role');
  const currentUsername = localStorage.getItem('username');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalClients, setTotalClients] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    totalClients: 0,
    uniqueProjectHeads: 0,
    totalPlotArea: 0,
    activeAreas: 0,
    inwardCount: 0,
    inProgressCount: 0,
    outwardCount: 0,
    completedCount: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [maxUploadMb, setMaxUploadMb] = useState(2048);
  const maxUploadLabel = maxUploadMb >= 1024 && maxUploadMb % 1024 === 0
    ? `${maxUploadMb / 1024} GB`
    : `${maxUploadMb} MB`;
  const [directCloudUpload, setDirectCloudUpload] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [fileLabelInput, setFileLabelInput] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [linkNameInput, setLinkNameInput] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const bulkUploadInputRef = useRef(null);
  const [filters, setFilters] = useState({
    customerArea: '',
    plotAreaUnit: '',
    projectHead: '',
    status: ''
  });
  const [architectUsers, setArchitectUsers] = useState([]);
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    mobile_number: '',
    customer_area: '',
    firm_name: '',
    plot_no: '',
    plot_area: '',
    plot_area_unit: 'sq.m',
    project_head: '',
    assigned_architect: currentRole === 'architect' ? currentUsername : '',
    status: 'inward',
    inward_date: '',
    outward_date: ''
  });

  const apiRoot = process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_API_URL || '')
    : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
  const API_BASE_URL = apiRoot ? `${apiRoot}/api` : '/api';

  // Customer area options
  const customerAreaOptions = ['MIDC', 'PMRDA', 'PCMC', 'TP', 'NMRDA', 'MSRDC'];
  const plotAreaUnitOptions = ['sq.m', 'sq.feet', 'acre'];

  const buildListParams = useCallback((pageNumber = page) => {
    const params = {
      page: pageNumber,
      limit: pageSize,
      sortBy: sortConfig.key || 'created_at',
      sortDir: sortConfig.direction || 'desc'
    };
    if (filters.customerArea) params.customerArea = filters.customerArea;
    if (filters.plotAreaUnit) params.plotAreaUnit = filters.plotAreaUnit;
    if (filters.projectHead) params.projectHead = filters.projectHead;
    if (filters.status) params.status = filters.status;
    if (searchTerm.trim()) params.search = searchTerm.trim();
    return params;
  }, [page, pageSize, sortConfig, filters, searchTerm]);

  const fetchClients = useCallback(async (pageNumber = page) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/clients`, {
        params: buildListParams(pageNumber)
      });
      setClients(response.data.data || []);
      setTotalClients(response.data.total || 0);
      setTotalPages(response.data.totalPages || 1);
      setPage(response.data.page || pageNumber);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error(error?.response?.data?.error || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, buildListParams, page]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clients/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [API_BASE_URL]);

  const openAttachment = async (clientId, attachmentId, download = false) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/clients/${clientId}/attachments/${attachmentId}/file`,
        {
          params: download ? { download: '1' } : {},
          responseType: 'blob'
        }
      );
      const blobUrl = URL.createObjectURL(response.data);
      if (download) {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = 'attachment';
        link.click();
      } else {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Error opening attachment:', error);
      toast.error('Failed to open attachment');
    }
  };

  const downloadClientDetailsPdf = async (clientRow) => {
    try {
      const [{ data: clientData }, { data: clientAttachments }] = await Promise.all([
        axios.get(`${API_BASE_URL}/clients/${clientRow.id}`),
        axios.get(`${API_BASE_URL}/clients/${clientRow.id}/attachments`)
      ]);
      await downloadClientPackage({
        client: clientData,
        attachments: clientAttachments || [],
        fetchAttachment: async (attachment) => {
          if (isExternalLink(attachment.attach_link)) {
            try {
              const response = await fetch(attachment.attach_link);
              if (!response.ok) {
                return { skipped: true, reason: 'External file not directly downloadable' };
              }
              const blob = await response.blob();
              return {
                blob,
                contentType: response.headers.get('content-type') || blob.type
              };
            } catch {
              return { skipped: true, reason: 'External file not directly downloadable' };
            }
          }

          const response = await axios.get(
            `${API_BASE_URL}/clients/${clientRow.id}/attachments/${attachment.id}/file`,
            {
              params: { download: '1' },
              responseType: 'blob'
            }
          );
          return {
            blob: response.data,
            contentType: response.headers['content-type'] || response.data?.type
          };
        }
      });
      toast.success('ZIP package downloaded');
    } catch (error) {
      console.error('Error generating client package:', error);
      toast.error('Failed to download ZIP package');
    }
  };

  const DEFAULT_ATTACH_TYPE = currentRole === 'architect' ? 'drawing' : 'document';
  const isArchitectUser = currentRole === 'architect';
  const canEditClients = currentRole === 'admin' || currentRole === 'architect';
  const attachmentLabelPlaceholder = isArchitectUser
    ? 'Architect Drawing label (optional)'
    : 'Document label (e.g. Floor plan, NOC, Site photo)';
  const attachmentSectionLabel = isArchitectUser ? 'Architect Drawing' : 'Attachments';

  const resetAttachmentState = () => {
    setAttachments([]);
    setFileLabelInput('');
    setLinkInput('');
    setLinkNameInput('');
    setUploadProgress(0);
  };

  const handleFilesSelected = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const maxBytes = maxUploadMb * 1024 * 1024;
    const label = fileLabelInput.trim();
    const newItems = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (file.size > maxBytes) {
        toast.error(`${file.name} exceeds ${maxUploadLabel}`);
        continue;
      }
      const attachName = label
        ? (files.length > 1 ? `${label} (${i + 1})` : label)
        : (isArchitectUser ? 'Architect Drawing' : file.name);
      newItems.push({
        localId: `${Date.now()}-${file.name}-${Math.random()}`,
        status: 'pending',
        file,
        attach_name: attachName,
        attach_type: DEFAULT_ATTACH_TYPE
      });
    }
    if (newItems.length) {
      setAttachments((prev) => [...prev, ...newItems]);
      setFileLabelInput('');
    }
    if (event.target) event.target.value = '';
  };

  const updateAttachmentLabel = (localId, attachName) => {
    setAttachments((prev) =>
      prev.map((item) =>
        item.localId === localId ? { ...item, attach_name: attachName } : item
      )
    );
  };

  const saveAttachmentLabel = async (item) => {
    const label = String(item.attach_name || '').trim();
    if (!label) {
      toast.error(isArchitectUser ? 'Drawing label is required' : 'Document label is required');
      return;
    }
    if (!item.id || !editingClient?.id) return;
    try {
      await axios.patch(
        `${API_BASE_URL}/clients/${editingClient.id}/attachments/${item.id}`,
        { attach_name: label }
      );
    } catch (error) {
      toast.error(isArchitectUser ? 'Failed to update drawing label' : 'Failed to update document label');
    }
  };

  const addExternalLink = () => {
    const url = linkInput.trim();
    if (!url) {
      toast.error('Enter a valid URL');
      return;
    }
    setAttachments((prev) => [
      ...prev,
      {
        localId: `link-${Date.now()}`,
        status: 'pending',
        attach_link: url,
        attach_name: linkNameInput.trim() || (isArchitectUser ? 'Architect Drawing' : url),
        attach_type: DEFAULT_ATTACH_TYPE
      }
    ]);
    setLinkInput('');
    setLinkNameInput('');
  };

  const removeAttachment = async (item) => {
    if (item.id && editingClient?.id) {
      try {
        await axios.delete(
          `${API_BASE_URL}/clients/${editingClient.id}/attachments/${item.id}`
        );
      } catch (error) {
        toast.error('Failed to remove attachment');
        return;
      }
    }
    setAttachments((prev) => prev.filter((a) => a.localId !== item.localId));
  };

  const uploadFileToPresignedUrl = (uploadUrl, file, headers, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      Object.entries(headers || {}).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded * 100) / event.total));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }
        reject(new Error(`Cloud upload failed (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Cloud upload failed'));
      xhr.send(file);
    });

  const uploadPendingAttachments = async (clientId) => {
    const pending = attachments.filter((a) => a.status === 'pending');
    if (!pending.length) return;

    setAttachmentUploading(true);
    try {
      for (const item of pending) {
        if (item.file) {
          if (directCloudUpload) {
            const presignResponse = await axios.post(
              `${API_BASE_URL}/clients/${clientId}/attachments/presign`,
              {
                filename: item.file.name,
                content_type: item.file.type || 'application/octet-stream',
                size_bytes: item.file.size,
                attach_type: DEFAULT_ATTACH_TYPE
              }
            );
            const { uploadUrl, headers, attach_link: attachLink } = presignResponse.data;
            await uploadFileToPresignedUrl(
              uploadUrl,
              item.file,
              headers,
              (percent) => setUploadProgress(percent)
            );
            await axios.post(`${API_BASE_URL}/clients/${clientId}/attachments/complete`, {
              attach_link: attachLink,
              attach_type: DEFAULT_ATTACH_TYPE,
              attach_name: item.attach_name || item.file.name,
              size_bytes: item.file.size
            });
          } else {
            const payload = new FormData();
            payload.append('files', item.file);
            payload.append('attach_type', DEFAULT_ATTACH_TYPE);
            payload.append('attach_name', item.attach_name || item.file.name);
            await axios.post(`${API_BASE_URL}/clients/${clientId}/attachments`, payload, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 0,
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
                }
              }
            });
          }
        } else if (item.attach_link) {
          await axios.post(`${API_BASE_URL}/clients/${clientId}/attachments`, {
            attach_link: item.attach_link,
            attach_type: DEFAULT_ATTACH_TYPE,
            attach_name: item.attach_name
          });
        }
      }
    } finally {
      setAttachmentUploading(false);
      setUploadProgress(0);
    }
  };

  const buildClientPayload = () => {
    const { attach_link, attach_type, attach_name, ...rest } = formData;
    return rest;
  };

  const fetchArchitectUsers = useCallback(async () => {
    if (currentRole !== 'admin') return;
    try {
      const response = await axios.get('/api/users');
      const architects = (response.data || []).filter((user) => user.role === 'architect');
      setArchitectUsers(architects);
    } catch (error) {
      console.error('Error fetching architects:', error);
      toast.error('Failed to load architect users');
    }
  }, [currentRole]);

  useEffect(() => {
    fetchClients(page);
  }, [page, sortConfig, filters.customerArea, filters.plotAreaUnit, filters.projectHead, filters.status]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/uploads/limits`)
      .then((res) => {
        setMaxUploadMb(res.data?.maxUploadMb || 2048);
        setDirectCloudUpload(Boolean(res.data?.directUpload));
      })
      .catch(() => {
        setMaxUploadMb(2048);
        setDirectCloudUpload(false);
      });
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchArchitectUsers();
  }, [fetchArchitectUsers]);

  const searchClients = async () => {
    setPage(1);
    fetchClients(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchClients();
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      customerArea: '',
      plotAreaUnit: '',
      projectHead: '',
      status: ''
    });
    setSearchTerm('');
    setSortConfig({ key: null, direction: 'asc' });
    setPage(1);
  };

  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const { saveAs } = await import('file-saver');
      const response = await axios.get(`${API_BASE_URL}/clients`, {
        params: { ...buildListParams(1), limit: 10000 }
      });
      const exportRows = response.data.data || [];
      const worksheet = XLSX.utils.json_to_sheet(exportRows.map(client => ({
      'Client ID': client.client_id,
      'Client Name': client.client_name,
      'Mobile Number': client.mobile_number,
      'Customer Area': client.customer_area,
      'Firm Name': client.firm_name,
      'Plot No': client.plot_no,
      'Plot Area': `${client.plot_area} ${client.plot_area_unit}`,
      'Project Head': client.project_head,
      'Assigned Architect': client.assigned_architect || '',
      'Status': formatClientStatusLabel(client.status),
      'Attachments': client.attachment_count || 0,
      'Inward Date': client.inward_date ? new Date(client.inward_date).toLocaleDateString() : '',
      'Outward Date': client.outward_date ? new Date(client.outward_date).toLocaleDateString() : '',
      'Created At': client.created_at ? new Date(client.created_at).toLocaleDateString() : ''
    })));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `clients_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Data exported to Excel successfully!');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Failed to export Excel');
    }
  };

  const normalizeExcelHeader = (header) =>
    String(header || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const parseExcelDate = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number') return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().split('T')[0];
  };

  const downloadBulkTemplate = async () => {
    try {
      const XLSX = await import('xlsx');
      const { saveAs } = await import('file-saver');
      const sampleRows = [
        {
          client_id: 'CL-2001',
          client_name: 'Sample Client',
          mobile_number: '9876543210',
          customer_area: 'MIDC',
          firm_name: 'Sample Firm Pvt Ltd',
          plot_no: 'A-101',
          plot_area: 250.5,
          plot_area_unit: 'sq.m',
          project_head: 'Rahul Mehta',
          assigned_architect: 'architect1',
          status: 'inward',
          inward_date: '2026-01-15',
          outward_date: '',
          attach_link: '',
          attach_name: ''
        }
      ];
      const worksheet = XLSX.utils.json_to_sheet(sampleRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([excelBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        'client_bulk_upload_template.xlsx'
      );
      toast.success('Bulk upload template downloaded');
    } catch (error) {
      console.error('Error creating bulk template:', error);
      toast.error('Failed to download template');
    }
  };

  const triggerBulkUpload = () => {
    if (currentRole !== 'admin') {
      toast.error('Bulk upload is available for admin only');
      return;
    }
    bulkUploadInputRef.current?.click();
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setBulkUploading(true);
      const XLSX = await import('xlsx');
      const isCsv = file.name.toLowerCase().endsWith('.csv');
      const workbook = isCsv
        ? XLSX.read(await file.text(), { type: 'string' })
        : XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];

      if (!firstSheetName) {
        toast.error('Excel file does not contain any sheet');
        return;
      }

      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      if (!rows.length) {
        toast.error('No data rows found in Excel file');
        return;
      }

      const transformedRows = rows.map((row) => {
        const mapped = {};
        Object.keys(row).forEach((header) => {
          mapped[normalizeExcelHeader(header)] = row[header];
        });
        return {
          client_id: String(mapped.client_id || '').trim(),
          client_name: String(mapped.client_name || '').trim(),
          mobile_number: String(mapped.mobile_number || '').trim(),
          customer_area: String(mapped.customer_area || '').trim(),
          firm_name: String(mapped.firm_name || '').trim(),
          plot_no: String(mapped.plot_no || '').trim(),
          plot_area: mapped.plot_area,
          plot_area_unit: String(mapped.plot_area_unit || '').trim(),
          project_head: String(mapped.project_head || '').trim(),
          assigned_architect: String(mapped.assigned_architect || '').trim(),
          attach_link: String(mapped.attach_link || '').trim(),
          attach_type: String(mapped.attach_type || '').trim(),
          attach_name: String(mapped.attach_name || '').trim(),
          inward_date: parseExcelDate(mapped.inward_date),
          outward_date: parseExcelDate(mapped.outward_date),
          status: String(mapped.status || '').trim()
        };
      });

      const response = await axios.post(`${API_BASE_URL}/clients/bulk`, { clients: transformedRows });
      const { imported, skipped, skippedRows } = response.data;

      if (imported > 0) {
        toast.success(`Bulk upload completed. Imported ${imported} clients.`);
      }
      if (skipped > 0) {
        toast.warn(`Skipped ${skipped} rows. Check console for details.`);
        console.table(skippedRows || []);
      }
      await fetchClients(page);
      await fetchStats();
    } catch (error) {
      console.error('Error uploading bulk clients:', error);
      toast.error(error?.response?.data?.error || 'Failed to upload Excel file');
    } finally {
      setBulkUploading(false);
      if (event.target) event.target.value = '';
    }
  };

  const openModal = async (client = null) => {
    if (isArchitectUser && !client) {
      toast.error('Architects can only upload Architect Drawings on assigned clients');
      return;
    }
    resetAttachmentState();
    if (client) {
      try {
        const [{ data: clientData }, { data: clientAttachments }] = await Promise.all([
          axios.get(`${API_BASE_URL}/clients/${client.id}`),
          axios.get(`${API_BASE_URL}/clients/${client.id}/attachments`)
        ]);
        client = clientData;
        setAttachments(
          (clientAttachments || []).map((a) => ({
            ...a,
            status: 'saved',
            localId: `saved-${a.id}`
          }))
        );
      } catch (error) {
        toast.error('Failed to load client details');
        return;
      }
      setEditingClient(client);

      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        } catch (error) {
          return '';
        }
      };

      setFormData({
        client_id: client.client_id,
        client_name: client.client_name,
        mobile_number: client.mobile_number,
        customer_area: client.customer_area,
        firm_name: client.firm_name,
        plot_no: client.plot_no,
        plot_area: client.plot_area,
        plot_area_unit: client.plot_area_unit,
        project_head: client.project_head,
        assigned_architect: client.assigned_architect || '',
        status: client.status || 'inward',
        inward_date: formatDateForInput(client.inward_date),
        outward_date: formatDateForInput(client.outward_date)
      });
    } else {
      setEditingClient(null);
      setFormData({
        client_id: '',
        client_name: '',
        mobile_number: '',
        customer_area: '',
        firm_name: '',
        plot_no: '',
        plot_area: '',
        plot_area_unit: 'sq.m',
        project_head: '',
        assigned_architect: currentRole === 'architect' ? currentUsername : '',
        status: 'inward',
        inward_date: '',
        outward_date: ''
      });
    }
    setShowModal(true);
  };

  useEffect(() => {
    const editClientId = location.state?.editClientId;
    if (!editClientId) return;
    if (currentRole !== 'admin' && currentRole !== 'architect') {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    openModal({ id: editClientId });
    navigate(location.pathname, { replace: true, state: {} });
    // One-shot open from client detail "Edit Client"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, navigate, currentRole]);

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
    resetAttachmentState();
    setFormData({
      client_id: '',
      client_name: '',
      mobile_number: '',
      customer_area: '',
      firm_name: '',
      plot_no: '',
      plot_area: '',
      plot_area_unit: 'sq.m',
      project_head: '',
      assigned_architect: currentRole === 'architect' ? currentUsername : '',
      status: 'inward',
      inward_date: '',
      outward_date: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isArchitectUser) {
      if (!editingClient?.id) {
        toast.error('Architects can only manage Architect Drawings on assigned clients');
        return;
      }
      try {
        await uploadPendingAttachments(editingClient.id);
        toast.success('Architect Drawing saved');
        closeModal();
        fetchClients(page);
        fetchStats();
      } catch (error) {
        console.error('Error saving architect drawing:', error);
        toast.error(error.response?.data?.error || 'Failed to save Architect Drawing');
      }
      return;
    }

    if (currentRole === 'admin' && !formData.assigned_architect) {
      toast.error('Assigned Architect is required');
      return;
    }

    try {
      const payload = buildClientPayload();
      let clientId;

      if (editingClient) {
        await axios.put(`${API_BASE_URL}/clients/${editingClient.id}`, payload);
        clientId = editingClient.id;
        toast.success('Client updated successfully');
      } else {
        const response = await axios.post(`${API_BASE_URL}/clients`, payload);
        clientId = response.data.id;
        toast.success('Client added successfully');
      }

      await uploadPendingAttachments(clientId);

      closeModal();
      fetchClients(page);
      fetchStats();
    } catch (error) {
      console.error('Error saving client:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save client';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/clients/${id}`);
      toast.success('Client deleted successfully');
      fetchClients(page);
      fetchStats();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client');
    }
  };

  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setMobileOpen]);


  const [showCreateUser, setShowCreateUser] = useState(false);

  const logout = () => {
    endSession();
  };

  return (
    <div className="App">
      <div className="app-shell">
        <AppSidebar
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          active="dashboard"
          onLogout={logout}
        />

        <div className="app-main">
          <header className="app-topbar">
            <div className="app-topbar-actions">
              <button
                type="button"
                className="btn btn-outline sidebar-toggle"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={18} />
                Menu
              </button>
              <h1 className="app-topbar-title">Dashboard</h1>
            </div>
            <div className="app-topbar-actions">
              <NotificationBell />
              <input
                ref={bulkUploadInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleBulkUpload}
              />
              {currentRole === 'admin' && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={downloadBulkTemplate}
                    title="Download Excel template"
                  >
                    <Download size={16} />
                    Template
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={triggerBulkUpload}
                    disabled={bulkUploading}
                  >
                    <Upload size={16} />
                    {bulkUploading ? 'Uploading…' : 'Bulk Upload'}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => openModal()}>
                    <Plus size={16} />
                    Add New Client
                  </button>
                </>
              )}
            </div>
          </header>

          <div className="container">
            <AdminCreateUserButton open={showCreateUser} onClose={() => setShowCreateUser(false)} />

            <div className="card">


          <div className="stats-panel">
            <section className="stats-section" aria-label="Overview statistics">
              <h2 className="stats-section-title">Overview</h2>
              <div className="stats-grid">
                {OVERVIEW_STATS.map((item) => {
                  const raw = stats[item.key];
                  const value = item.format ? item.format(raw) : (raw ?? 0);
                  return (
                    <StatCard
                      key={item.key}
                      icon={item.icon}
                      label={item.label}
                      value={value}
                      className="stat-card--primary"
                    />
                  );
                })}
              </div>
            </section>

            <section className="stats-section" aria-label="Client status pipeline">
              <h2 className="stats-section-title">Client pipeline</h2>
              <div className="stats-grid">
                {CLIENT_STATUS_OPTIONS.map((option) => {
                  const countKey = option.value === 'in_progress'
                    ? 'inProgressCount'
                    : `${option.value}Count`;
                  return (
                    <StatCard
                      key={option.value}
                      icon={option.icon}
                      label={option.label}
                      value={stats[countKey] || 0}
                      className={`stat-card--${option.value}`}
                    />
                  );
                })}
              </div>
            </section>
          </div>

          {/* Search Section */}
          <div className="search-container">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input">
                <input
                  type="text"
                  placeholder="Search by Client ID, Name, Mobile, Area, Firm, Plot No, Plot Area, or Project Head..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control"
                />
              </div>
              <button type="submit" className="btn btn-primary">
                <Search size={16} />
                Search
              </button>
              {(searchTerm || Object.values(filters).some(f => f)) && (
                <button type="button" className="btn btn-secondary" onClick={clearFilters}>
                  <X size={16} />
                  Clear All
                </button>
              )}
            </form>

            {/* Filter Controls */}
            <div className="filter-controls">
              <div className="filter-group">
                <label>Customer Area:</label>
                <select
                  className="filter-select"
                  value={filters.customerArea}
                  onChange={(e) => handleFilterChange('customerArea', e.target.value)}
                >
                  <option value="">All Areas</option>
                  {customerAreaOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Plot Unit:</label>
                <select
                  className="filter-select"
                  value={filters.plotAreaUnit}
                  onChange={(e) => handleFilterChange('plotAreaUnit', e.target.value)}
                >
                  <option value="">All Units</option>
                  {plotAreaUnitOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Project Head:</label>
                <input
                  type="text"
                  className="filter-select"
                  placeholder="Filter by project head..."
                  value={filters.projectHead}
                  onChange={(e) => handleFilterChange('projectHead', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label>Status:</label>
                <select
                  className="filter-select"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {CLIENT_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Table Section */}
          {loading ? (
            <div className="loading">Loading clients...</div>
          ) : (
            <div className="table-container">
              <div className="table-header">
                <div className="table-title">
                  <FileText size={20} />
                  Client Records ({clients.length} of {totalClients})
                </div>
                <div className="table-actions">
                  <button type="button" className="btn btn-outline" onClick={exportToExcel}>
                    <Download size={16} />
                    Export Excel
                  </button>
                </div>
              </div>
              <div className="table-wrapper">
                <table className="table">
                <thead>
                  <tr>
                    <th
                      className="sortable"
                      onClick={() => handleSort('client_id')}
                    >
                      Client ID
                      {sortConfig.key === 'client_id' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('client_name')}
                    >
                      Client Name
                      {sortConfig.key === 'client_name' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('mobile_number')}
                    >
                      Mobile Number
                      {sortConfig.key === 'mobile_number' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('customer_area')}
                    >
                      Customer Area
                      {sortConfig.key === 'customer_area' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('firm_name')}
                    >
                      Firm Name
                      {sortConfig.key === 'firm_name' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('plot_no')}
                    >
                      Plot No
                      {sortConfig.key === 'plot_no' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('plot_area')}
                    >
                      Plot Area
                      {sortConfig.key === 'plot_area' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('project_head')}
                    >
                      Project Head
                      {sortConfig.key === 'project_head' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('assigned_architect')}
                    >
                      Assigned Architect
                      {sortConfig.key === 'assigned_architect' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('status')}
                    >
                      Status
                      {sortConfig.key === 'status' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th>Attachments</th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('inward_date')}
                    >
                      Inward Date
                      {sortConfig.key === 'inward_date' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleSort('outward_date')}
                    >
                      Outward Date
                      {sortConfig.key === 'outward_date' && (
                        sortConfig.direction === 'asc' ? <SortAsc size={12} /> : <SortDesc size={12} />
                      )}
                    </th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length === 0 ? (
                    <tr>
                      <td colSpan="14">
                        <div className="empty-state">
                          <FileText size={64} />
                          <h3>No clients found</h3>
                          <p>Try adjusting your search or filters to find what you're looking for.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    clients.map(client => (
                      <tr key={client.id}>
                        <td>
                          <div className="cell-content">
                            {client.client_id}
                          </div>
                        </td>
                        <td>
                          <div className="cell-content">
                            <Users size={14} />
                            {client.client_name}
                          </div>
                        </td>
                        <td>
                          <div className="cell-content">
                            <Phone size={14} />
                            {client.mobile_number}
                          </div>
                        </td>
                        <td>
                          <div className="cell-content">
                            <MapPin size={14} />
                            {client.customer_area}
                          </div>
                        </td>
                        <td>
                          <div className="cell-content">
                            <Building size={14} />
                            {client.firm_name}
                          </div>
                        </td>
                        <td>{client.plot_no}</td>
                        <td>
                          <div className="cell-content">
                            <Target size={14} />
                            <span>{client.plot_area} {client.plot_area_unit}</span>
                          </div>
                        </td>
                        <td>
                          <div className="cell-content">
                            <Users size={14} />
                            {client.project_head}
                          </div>
                        </td>
                        <td>{client.assigned_architect || '-'}</td>
                        <td>
                          <ClientStatusBadge status={client.status} />
                        </td>
                        <td>
                          <div className="cell-content attachment-cell">
                            {Number(client.attachment_count) > 0 ? (
                              <button
                                type="button"
                                className="btn btn-link link-cell"
                                onClick={() => navigate(`/clients/${client.id}`)}
                              >
                                <FileText size={14} />
                                {client.attachment_count} file(s)
                              </button>
                            ) : (
                              <span className="no-link">No files</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="cell-content">
                            <Calendar size={14} />
                            {client.inward_date ? new Date(client.inward_date).toLocaleDateString() : 'Not set'}
                          </div>
                        </td>
                        <td>
                          <div className="cell-content">
                            <Calendar size={14} />
                            {client.outward_date ? new Date(client.outward_date).toLocaleDateString() : 'Not set'}
                          </div>
                        </td>
                        <td className="col-actions">
                          <div className="actions">
                            <button
                              className="btn btn-primary"
                              onClick={() => navigate(`/clients/${client.id}`)}
                              title="View"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              className="btn btn-outline"
                              onClick={() => downloadClientDetailsPdf(client)}
                              title="Download ZIP"
                            >
                              <FileDown size={14} />
                            </button>
                            {canEditClients && (
                              <button
                                className="btn btn-secondary"
                                onClick={() => openModal(client)}
                                title={isArchitectUser ? 'Architect Drawing' : 'Edit'}
                              >
                                <Edit size={14} />
                              </button>
                            )}
                            {currentRole === 'admin' && (
                              <button
                                className="btn btn-danger"
                                onClick={() => handleDelete(client.id)}
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
              <div className="pagination-bar">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {page} of {totalPages} ({totalClients} records)
                </span>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                {isArchitectUser
                  ? 'Architect Drawing'
                  : (editingClient ? 'Edit Client' : 'Add New Client')}
              </h2>
              <button className="close-btn" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              {isArchitectUser ? (
                <div className="form-group">
                  <label className="form-label">Client</label>
                  <div className="form-control" style={{ background: '#f7fafc' }}>
                    <strong>{formData.client_name || '—'}</strong>
                    {formData.client_id ? ` · ${formData.client_id}` : ''}
                  </div>
                  <small style={{ color: '#6c757d', display: 'block', marginTop: '6px' }}>
                    You can only upload and edit Architect Drawings. Other client details are read-only.
                  </small>
                </div>
              ) : (
              <>
              <div className="form-group">
                <label className="form-label">Client ID *</label>
                <input
                  type="text"
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                  placeholder="e.g., Vision 101"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                  placeholder="e.g., MRS GEETA WARKEY"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Mobile Number *</label>
                <input
                  type="tel"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                  placeholder="e.g., 7798777718"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Customer Area *</label>
                <input
                  type="text"
                  name="customer_area"
                  value={formData.customer_area}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                  maxLength={255}
                  placeholder="e.g., MIDC"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Firm Name *</label>
                <input
                  type="text"
                  name="firm_name"
                  value={formData.firm_name}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                  placeholder="e.g., M/S NUTRILITE FOODE PVT LTD"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Plot No *</label>
                <input
                  type="text"
                  name="plot_no"
                  value={formData.plot_no}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                  placeholder="e.g., A-16"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Plot Area *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    name="plot_area"
                    value={formData.plot_area}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                    placeholder="e.g., 1000"
                    style={{ flex: 2 }}
                  />
                  <select
                    name="plot_area_unit"
                    value={formData.plot_area_unit}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                    style={{ flex: 1 }}
                  >
                    {plotAreaUnitOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Project Head *</label>
                <input
                  type="text"
                  name="project_head"
                  value={formData.project_head}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                  placeholder="e.g., John Doe"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Assigned Architect *</label>
                {currentRole === 'admin' ? (
                  <select
                    name="assigned_architect"
                    value={formData.assigned_architect}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="">Select architect</option>
                    {architectUsers.map((user) => (
                      <option key={user.id} value={user.username}>{user.username}</option>
                    ))}
                  </select>
                ) : (
                  <input className="form-control" value={currentUsername || ''} disabled />
                )}
              </div>
              </>
              )}
              <div className="form-group">
                <label className="form-label">{attachmentSectionLabel}</label>
                <input
                  type="text"
                  className="form-control"
                  value={fileLabelInput}
                  onChange={(e) => setFileLabelInput(e.target.value)}
                  placeholder={attachmentLabelPlaceholder}
                  style={{ marginBottom: '8px' }}
                />
                <input
                  type="file"
                  multiple
                  onChange={handleFilesSelected}
                  className="form-control"
                  disabled={attachmentUploading}
                />
                <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>
                  {isArchitectUser
                    ? `Upload Architect Drawing only — max ${maxUploadLabel} each, up to 20 per client. Admin will be notified by email.`
                    : `Add a label, then choose files — max ${maxUploadLabel} each, up to 20 per client`}
                </small>
                {attachmentUploading && uploadProgress > 0 && (
                  <small style={{ color: '#6c757d', display: 'block', marginTop: '4px' }}>
                    Uploading… {uploadProgress}%
                  </small>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <input
                    type="url"
                    className="form-control"
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    placeholder={isArchitectUser ? 'Architect Drawing link (Drive, etc.)' : 'External link (Google Drive, etc.)'}
                  />
                  <input
                    type="text"
                    className="form-control"
                    value={linkNameInput}
                    onChange={(e) => setLinkNameInput(e.target.value)}
                    placeholder={isArchitectUser ? 'Drawing label' : 'Document label'}
                    style={{ maxWidth: '180px' }}
                  />
                  <button type="button" className="btn btn-secondary" onClick={addExternalLink}>
                    Add Link
                  </button>
                </div>
                {attachments.length > 0 && (
                  <ul className="attachment-list">
                    {attachments
                      .filter((item) => !isArchitectUser || item.attach_type === 'drawing' || item.status === 'pending')
                      .map((item) => {
                      const canManageDrawing = !isArchitectUser || item.attach_type === 'drawing' || item.status === 'pending';
                      return (
                      <li key={item.localId} className="attachment-list-item">
                        <div className="attachment-list-meta">
                          <FileText size={14} className="attachment-list-icon" />
                          <input
                            type="text"
                            className="form-control attachment-label-input"
                            value={item.attach_name || ''}
                            onChange={(e) => updateAttachmentLabel(item.localId, e.target.value)}
                            onBlur={() => saveAttachmentLabel(item)}
                            placeholder={isArchitectUser ? 'Drawing label' : 'Document label'}
                            aria-label={isArchitectUser ? 'Drawing label' : 'Document label'}
                            disabled={!canManageDrawing}
                          />
                          {item.attach_type === 'drawing' && (
                            <span className="no-link attachment-pending-tag">Architect Drawing</span>
                          )}
                          {item.status === 'pending' && (
                            <span className="no-link attachment-pending-tag">pending</span>
                          )}
                          {item.file && (
                            <span className="no-link attachment-file-name" title={item.file.name}>
                              {item.file.name}
                            </span>
                          )}
                          {item.attach_type === 'drawing' && item.remark && (
                            <span className="no-link attachment-file-name" title={item.remark}>
                              Remark: {item.remark}
                            </span>
                          )}
                        </div>
                        <span className="attachment-list-actions">
                          {item.id && editingClient && (
                            <>
                              <button
                                type="button"
                                className="btn btn-link"
                                onClick={() => openAttachment(editingClient.id, item.id, false)}
                              >
                                Open
                              </button>
                              <button
                                type="button"
                                className="btn btn-link"
                                onClick={() => openAttachment(editingClient.id, item.id, true)}
                              >
                                Download
                              </button>
                            </>
                          )}
                          {canManageDrawing && (
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => removeAttachment(item)}
                              style={{ padding: '2px 8px', fontSize: '12px' }}
                            >
                              Remove
                            </button>
                          )}
                        </span>
                      </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {!isArchitectUser && (
              <>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  {CLIENT_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Inward Date</label>
                <input
                  type="date"
                  name="inward_date"
                  value={formData.inward_date}
                  onChange={handleInputChange}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Outward Date</label>
                <input
                  type="date"
                  name="outward_date"
                  value={formData.outward_date}
                  onChange={handleInputChange}
                  className="form-control"
                />
              </div>
              </>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={attachmentUploading}>
                  {isArchitectUser
                    ? 'Save Architect Drawing'
                    : (editingClient ? 'Update Client' : 'Add Client')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="crm-toast"
      />
    </div>
  );
}

export default App;
